import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import { useGraphData, useFilteredGraphData } from '@/hooks/useGraph';
import { useCapabilities } from '@/hooks/useCapabilities';
import AppShell from '@/components/layout/AppShell';
import GraphView from '@/components/graph/GraphView';
import GraphControls from '@/components/graph/GraphControls';
import AIPanel from '@/components/ai/AIPanel';
import { CapabilityType, CapabilityStatus } from '@/types';

const GraphPage: React.FC = () => {
  const history = useHistory();
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const graphFilterTypes = useUIStore((s) => s.graphFilterTypes);
  const graphFilterStatuses = useUIStore((s) => s.graphFilterStatuses);
  const activePanel = useUIStore((s) => s.activePanel);
  const setActivePanel = useUIStore((s) => s.setActivePanel);
  const selectedCapabilityId = useUIStore((s) => s.selectedCapabilityId);

  const workspaceId = activeWorkspace?.id ?? null;

  // Load capabilities so nodes have data
  useCapabilities(workspaceId, { pageSize: 200 });

  const { graphData, isLoading } = useGraphData(workspaceId);
  const filteredData = useFilteredGraphData(graphData, graphFilterTypes, graphFilterStatuses);

  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);

  const handleNodeClick = (nodeId: string) => {
    const node = Object.values(useWorkspaceStore.getState().nodes).find((n) => n.id === nodeId);
    if (node) {
      useUIStore.getState().setSelectedCapabilityId(node.capabilityId);
    }
  };

  const handleNodeDblClick = (nodeId: string) => {
    const node = Object.values(useWorkspaceStore.getState().nodes).find((n) => n.id === nodeId);
    if (node) {
      history.push(`/capabilities/${node.capabilityId}`);
    }
  };

  return (
    <AppShell>
      <div className="relative flex h-full w-full overflow-hidden">
            {/* Filter sidebar */}
            {filterSidebarOpen && (
              <aside className="absolute left-0 top-0 bottom-0 z-20 w-64 bg-white dark:bg-surface-900 border-r border-surface-200 dark:border-surface-800 shadow-panel flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-800">
                  <h3 className="text-sm font-semibold text-surface-900 dark:text-white">Filters</h3>
                  <button
                    type="button"
                    onClick={() => setFilterSidebarOpen(false)}
                    className="p-1 rounded hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500"
                    aria-label="Close filters"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-5">
                  {/* Type filter */}
                  <div>
                    <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">
                      Type
                    </p>
                    <div className="space-y-1">
                      {Object.values(CapabilityType).map((type) => {
                        const isActive = graphFilterTypes.includes(type);
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => useUIStore.getState().toggleGraphFilterType(type)}
                            className={[
                              'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-left',
                              isActive
                                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800',
                            ].join(' ')}
                          >
                            <span
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-primary-500' : 'bg-surface-300 dark:bg-surface-600'}`}
                            />
                            {type}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Status filter */}
                  <div>
                    <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">
                      Status
                    </p>
                    <div className="space-y-1">
                      {Object.values(CapabilityStatus).map((status) => {
                        const isActive = graphFilterStatuses.includes(status);
                        const statusColors: Record<string, string> = {
                          active: 'bg-green-400',
                          growing: 'bg-blue-400',
                          dormant: 'bg-yellow-400',
                          deprecated: 'bg-red-400',
                        };
                        return (
                          <button
                            key={status}
                            type="button"
                            onClick={() => {
                              const current = useUIStore.getState().graphFilterStatuses;
                              useUIStore.getState().setGraphFilterStatuses(
                                current.includes(status)
                                  ? current.filter((s) => s !== status)
                                  : [...current, status]
                              );
                            }}
                            className={[
                              'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-left',
                              isActive
                                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800',
                            ].join(' ')}
                          >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColors[status] ?? 'bg-surface-300'}`} />
                            {status}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="pt-2 border-t border-surface-200 dark:border-surface-800">
                    <p className="text-2xs text-surface-400">
                      Showing {filteredData.nodes.length} of {graphData.nodes.length} nodes
                    </p>
                    <p className="text-2xs text-surface-400">
                      {filteredData.edges.length} connections
                    </p>
                  </div>
                </div>

                {(graphFilterTypes.length > 0 || graphFilterStatuses.length > 0) && (
                  <div className="p-4 border-t border-surface-200 dark:border-surface-800">
                    <button
                      type="button"
                      onClick={() => {
                        useUIStore.getState().setGraphFilterTypes([]);
                        useUIStore.getState().setGraphFilterStatuses([]);
                      }}
                      className="w-full py-2 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </aside>
            )}

            {/* Graph canvas */}
            <div className="flex-1 relative">
              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-surface-50 dark:bg-surface-950">
                  <div className="flex flex-col items-center gap-3">
                    <svg className="animate-spin w-8 h-8 text-primary-500" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="text-sm text-surface-500">Building graph…</p>
                  </div>
                </div>
              ) : graphData.nodes.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-xl bg-surface-800 border border-surface-700 flex items-center justify-center mb-4 mx-auto">
                      <svg className="w-7 h-7 text-surface-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                        <circle cx="12" cy="12" r="3"/><circle cx="5" cy="6" r="2"/><circle cx="19" cy="6" r="2"/><circle cx="5" cy="18" r="2"/><circle cx="19" cy="18" r="2"/>
                        <line x1="12" y1="12" x2="5" y2="6"/><line x1="12" y1="12" x2="19" y2="6"/>
                        <line x1="12" y1="12" x2="5" y2="18"/><line x1="12" y1="12" x2="19" y2="18"/>
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-surface-700 dark:text-surface-300">
                      No capabilities in graph
                    </p>
                    <p className="text-sm text-surface-500 mt-1">
                      Add capabilities to start building your knowledge graph
                    </p>
                  </div>
                </div>
              ) : (
                <GraphView
                  graphData={filteredData}
                  onNodeClick={handleNodeClick}
                  onNodeDblClick={handleNodeDblClick}
                />
              )}

              {/* Overlay controls */}
              <GraphControls
                onToggleFilters={() => setFilterSidebarOpen((v) => !v)}
                filterCount={graphFilterTypes.length + graphFilterStatuses.length}
                nodeCount={filteredData.nodes.length}
                edgeCount={filteredData.edges.length}
              />
            </div>

            {/* AI Panel (slides in from right) */}
            {activePanel === 'ai-suggestions' && selectedCapabilityId && (
              <AIPanel
                workspaceId={workspaceId ?? ''}
                capabilityId={selectedCapabilityId}
                onClose={() => setActivePanel('none')}
              />
            )}
      </div>
    </AppShell>
  );
};

export default GraphPage;
